import React, { useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "../../services/auth";
import {
  createMember,
  getKanda,
  getJumuia,
  getSmsBalance,
} from "../../services/api";

import {
  TZ_LOCATIONS,
  REGIONS,
  AGE_GROUPS,
  EVENT_OPTIONS,
} from "./tzLocations";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function RegisterMember({
  onSuccess,
  onClose,
}) {
  const user = getCurrentUser();

  const [parokiaId] = useState(
    user?.parokiaId || null
  );

  const [parokiaName, setParokiaName] =
    useState(user?.parokiaName || "");

  const [jimboName, setJimboName] =
    useState(user?.jimboName || "");

  const [kandaList, setKandaList] =
    useState([]);

  const [jumuiaList, setJumuiaList] =
    useState([]);

  const [loadingKanda, setLoadingKanda] =
    useState(false);

  const [loadingJumuia, setLoadingJumuia] =
    useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [smsBalance, setSmsBalance] =
    useState(0);

  const [form, setForm] = useState({
    firstName: "",
    middleName: "",
    surname: "",
    gender: "MALE",
    dateOfBirth: "",
    phone: "",
    backupPhone: "",
    maritalStatus: "",
    residenceArea: "",
    address: "",
    countryName: "Tanzania",
    regionName: "",
    districtName: "",
    isStudent: false,
    isJumuiaLeader: false,
    eventName: "",
    ageGroup: "",
    notes: "",
    kandaId: "",
    jumuiaId: "",
  });

  const districtOptions = useMemo(() => {
    return (
      TZ_LOCATIONS[form.regionName] || []
    );
  }, [form.regionName]);

  useEffect(() => {
    if (!parokiaId) return;

    loadKandaList();
    loadSmsBalance();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parokiaId]);

  useEffect(() => {
    if (!parokiaId || !form.kandaId) {
      setJumuiaList([]);

      setForm((prev) => ({
        ...prev,
        jumuiaId: "",
      }));

      return;
    }

    loadJumuiaList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parokiaId, form.kandaId]);

  const loadSmsBalance = async () => {
    try {
      const res = await getSmsBalance();

      setSmsBalance(
        Number( res?.data?.balance || 0 )
      );
    } catch (err) {
      console.error(
        "Failed to load SMS balance",
        err
      );
    }
  };

  const loadKandaList = async () => {
    try {
      setLoadingKanda(true);

      const res = await getKanda(
        parokiaId
      );

      const rows = Array.isArray(
        res.data
      )
        ? res.data
        : [];

      setKandaList(rows);

      if (!jimboName && rows.length > 0) {
        setJimboName(
          rows[0].jimbo_name || ""
        );
      }

      if (
        !parokiaName &&
        rows.length > 0
      ) {
        setParokiaName(
          rows[0].parokia_name || ""
        );
      }
    } catch (err) {
      console.error(
        "Failed to load kanda",
        err
      );

      setKandaList([]);

      toast.error(
        "Failed to load Kanda"
      );
    } finally {
      setLoadingKanda(false);
    }
  };

  const loadJumuiaList = async () => {
    try {
      setLoadingJumuia(true);

      const res = await getJumuia(
        parokiaId,
        form.kandaId
      );

      setJumuiaList(
        Array.isArray(res.data)
          ? res.data
          : []
      );
    } catch (err) {
      console.error(
        "Failed to load jumuia",
        err
      );

      setJumuiaList([]);

      toast.error(
        "Failed to load Jumuia"
      );
    } finally {
      setLoadingJumuia(false);
    }
  };

  const normalizePhone = (phone) => {
    if (!phone) return "";

    let cleaned = String(phone)
      .replace(/\s+/g, "")
      .replace(/[^0-9]/g, "");

    if (cleaned.startsWith("255")) {
      return cleaned;
    }

    if (cleaned.startsWith("0")) {
      return `255${cleaned.substring(1)}`;
    }

    if (cleaned.length === 9) {
      return `255${cleaned}`;
    }

    return cleaned;
  };

  const setField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const submit = async () => {
    if (smsBalance <= 0) {
      toast.error(
        "SMS balance exhausted. Please top up SMS balance."
      );
      return;
    }

    if (
      !form.firstName ||
      !form.surname ||
      !form.dateOfBirth ||
      !form.phone ||
      !form.kandaId ||
      !form.jumuiaId
    ) {
      toast.error(
        "Please fill all required fields"
      );

      return;
    }

    const payload = {
      parokiaId,

      kandaId: Number(form.kandaId),

      jumuiaId: Number(
        form.jumuiaId
      ),

      firstName:
        form.firstName.trim(),

      middleName:
        form.middleName?.trim() ||
        null,

      surname:
        form.surname.trim(),

      gender: form.gender,

      dateOfBirth:
        form.dateOfBirth,

      phone:
        normalizePhone(form.phone) ||
        null,

      backupPhone:
        form.backupPhone?.trim() ||
        null,

      maritalStatus:
        form.maritalStatus || null,

      residenceArea:
        form.residenceArea?.trim() ||
        null,

      address:
        form.address?.trim() || null,

      countryName:
        form.countryName ||
        "Tanzania",

      regionName:
        form.regionName || null,

      districtName:
        form.districtName || null,

      isStudent: form.isStudent,

      isJumuiaLeader:
        form.isJumuiaLeader,

      eventName:
        form.eventName || null,

      ageGroup:
        form.ageGroup || null,

      notes:
        form.notes?.trim() || null,
    };

    try {
      setSubmitting(true);

      const res = await createMember(
        payload
      );

      const smsStatus =
        res?.data?.smsStatus;

      if (smsStatus === "SENT") {
        toast.success(
          "Member registered successfully and SMS sent"
        );
      } else if (
        smsStatus === "FAILED"
      ) {
        toast.warning(
          "Member registered successfully but SMS failed"
        );
      } else if (
        smsStatus === "NO_BALANCE"
      ) {
        toast.warning(
          "Member registered successfully but SMS balance is exhausted"
        );
      } else if (
        smsStatus === "NOT_SENT"
      ) {
        toast.info(
          "Member registered successfully but SMS was not sent"
        );
      } else {
        toast.success(
          "Member registered successfully"
        );
      }

      setForm({
        firstName: "",
        middleName: "",
        surname: "",
        gender: "MALE",
        dateOfBirth: "",
        phone: "",
        backupPhone: "",
        maritalStatus: "",
        residenceArea: "",
        address: "",
        countryName: "Tanzania",
        regionName: "",
        districtName: "",
        isStudent: false,
        isJumuiaLeader: false,
        eventName: "",
        ageGroup: "",
        notes: "",
        kandaId: "",
        jumuiaId: "",
      });

      setJumuiaList([]);

      loadSmsBalance();

      setTimeout(() => {
        onSuccess?.();
      }, 500);
    } catch (err) {
      console.error(
        err?.response?.data || err
      );

      toast.error(
        err?.response?.data?.message ||
          "Failed to register member"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const FieldLabel = ({
    children,
    required = false,
  }) => (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        marginBottom: 6,
        color: required
          ? "#dc2626"
          : "#374151",
      }}
    >
      {children}
    </div>
  );

  return (
    <div style={{ position: "relative" }}>
      {submitting && (
        <div className="transaction-processing-overlay">
          <div className="transaction-processing-box">
            <div className="spinner large" />

            <div className="processing-title">
              Registering Member...
            </div>

            <div className="processing-subtitle">
              Please wait while member
              registration is being
              processed
            </div>
          </div>
        </div>
      )}

      <h3 style={{ marginBottom: 16 }}>
        Register Member
      </h3>

      <div
        className="modal-section"
        style={{
          maxHeight: "68vh",
          overflowY: "auto",
          paddingRight: "10px",
          paddingBottom: "12px",
        }}
      >
        <div className="modal-section">
          <h4>Basic Information</h4>

          <div className="form-grid">
            <div>
              <FieldLabel>
                Jimbo
              </FieldLabel>

              <input
                className="input"
                value={jimboName || "-"}
                readOnly
              />
            </div>

            <div>
              <FieldLabel>
                Parokia
              </FieldLabel>

              <input
                className="input"
                value={
                  parokiaName || "-"
                }
                readOnly
              />
            </div>

            <div>
              <FieldLabel required>
                First Name
              </FieldLabel>

              <input
                className="input"
                value={form.firstName}
                onChange={(e) =>
                  setField(
                    "firstName",
                    e.target.value
                  )
                }
                placeholder="Enter first name"
              />
            </div>

            <div>
              <FieldLabel>
                Middle Name
              </FieldLabel>

              <input
                className="input"
                value={form.middleName}
                onChange={(e) =>
                  setField(
                    "middleName",
                    e.target.value
                  )
                }
                placeholder="Enter middle name"
              />
            </div>

            <div>
              <FieldLabel required>
                Surname
              </FieldLabel>

              <input
                className="input"
                value={form.surname}
                onChange={(e) =>
                  setField(
                    "surname",
                    e.target.value
                  )
                }
                placeholder="Enter surname"
              />
            </div>

            <div>
              <FieldLabel required>
                Gender
              </FieldLabel>

              <select
                className="input"
                value={form.gender}
                onChange={(e) =>
                  setField(
                    "gender",
                    e.target.value
                  )
                }
              >
                <option value="MALE">
                  Male
                </option>

                <option value="FEMALE">
                  Female
                </option>
              </select>
            </div>

            <div>
              <FieldLabel required>
                Birth Date
              </FieldLabel>

              <input
                type="date"
                className="input"
                value={form.dateOfBirth}
                onChange={(e) =>
                  setField(
                    "dateOfBirth",
                    e.target.value
                  )
                }
              />
            </div>

            <div>
              <FieldLabel required>
                Account Mobile No
              </FieldLabel>

              <input
                className="input"
                value={form.phone}
                onChange={(e) =>
                  setField(
                    "phone",
                    e.target.value
                  )
                }
                placeholder="Enter mobile number"
              />
            </div>
          </div>
        </div>

        <div className="modal-section">
          <h4>
            Address Information
          </h4>

          <div className="form-grid">
            <div>
              <FieldLabel>
                Country Name
              </FieldLabel>

              <input
                className="input"
                value="Tanzania"
                readOnly
              />
            </div>

            <div>
              <FieldLabel>
                Region Name
              </FieldLabel>

              <select
                className="input"
                value={form.regionName}
                onChange={(e) => {
                  setField(
                    "regionName",
                    e.target.value
                  );

                  setField(
                    "districtName",
                    ""
                  );
                }}
              >
                <option value="">
                  --Choose Region--
                </option>

                {REGIONS.map(
                  (region) => (
                    <option
                      key={region}
                      value={region}
                    >
                      {region}
                    </option>
                  )
                )}
              </select>
            </div>

            <div>
              <FieldLabel>
                District Name
              </FieldLabel>

              <select
                className="input"
                value={form.districtName}
                onChange={(e) =>
                  setField(
                    "districtName",
                    e.target.value
                  )
                }
                disabled={
                  !form.regionName
                }
              >
                <option value="">
                  {form.regionName
                    ? "--Choose District--"
                    : "Select region first"}
                </option>

                {districtOptions.map(
                  (district) => (
                    <option
                      key={district}
                      value={district}
                    >
                      {district}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>
        </div>

        <div className="modal-section">
          <h4>
            Catholic Member
            Categorization
          </h4>

          <div className="form-grid">
            <div>
              <FieldLabel required>
                Kanda Name
              </FieldLabel>

              <select
                className="input"
                value={form.kandaId}
                onChange={(e) =>
                  setField(
                    "kandaId",
                    e.target.value
                  )
                }
                disabled={loadingKanda}
              >
                <option value="">
                  {loadingKanda
                    ? "Loading Kanda..."
                    : "--Choose Kanda--"}
                </option>

                {kandaList.map((k) => (
                  <option
                    key={k.kanda_id}
                    value={k.kanda_id}
                  >
                    {k.kanda_name ||
                      k.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <FieldLabel required>
                Jumuiya Name
              </FieldLabel>

              <select
                className="input"
                value={form.jumuiaId}
                onChange={(e) =>
                  setField(
                    "jumuiaId",
                    e.target.value
                  )
                }
                disabled={
                  loadingJumuia ||
                  !form.kandaId
                }
              >
                <option value="">
                  {loadingJumuia
                    ? "Loading Jumuia..."
                    : !form.kandaId
                    ? "Select Kanda first"
                    : "--Choose Jumuia--"}
                </option>

                {jumuiaList.map((j) => (
                  <option
                    key={j.jumuia_id}
                    value={j.jumuia_id}
                  >
                    {j.jumuia_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <FieldLabel>
                Marital Status
              </FieldLabel>

              <select
                className="input"
                value={
                  form.maritalStatus
                }
                onChange={(e) =>
                  setField(
                    "maritalStatus",
                    e.target.value
                  )
                }
              >
                <option value="">
                  --Select Marital
                  Status--
                </option>

                <option value="SINGLE">
                  Single
                </option>

                <option value="MARRIED">
                  Married
                </option>

                <option value="DIVORCED">
                  Divorced
                </option>

                <option value="WIDOWED">
                  Widowed
                </option>
              </select>
            </div>

            <div>
              <FieldLabel>
                Member is Student
              </FieldLabel>

              <div
                style={{
                  height: 36,
                  display: "flex",
                  alignItems:
                    "center",
                  gap: 10,
                  border:
                    "1px solid #e2e8f0",
                  borderRadius: 6,
                  padding:
                    "0 12px",
                  background: "#fff",
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    form.isStudent
                  }
                  onChange={(e) =>
                    setField(
                      "isStudent",
                      e.target.checked
                    )
                  }
                />

                <span>Yes</span>
              </div>
            </div>

            <div>
              <FieldLabel>
                Age Group
              </FieldLabel>

              <select
                className="input"
                value={form.ageGroup}
                onChange={(e) =>
                  setField(
                    "ageGroup",
                    e.target.value
                  )
                }
              >
                <option value="">
                  --Select Age Group--
                </option>

                {AGE_GROUPS.map(
                  (age) => (
                    <option
                      key={age}
                      value={age}
                    >
                      {age}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>
        </div>

        <div className="modal-section">
          <h4>
            Additional Notes
          </h4>

          <textarea
            className="input"
            style={{
              minHeight: 100,
              paddingTop: 10,
            }}
            value={form.notes}
            onChange={(e) =>
              setField(
                "notes",
                e.target.value
              )
            }
            placeholder="Enter additional notes"
          />
        </div>
      </div>

      <div
        className="modal-actions"
        style={{
          position: "sticky",
          bottom: 0,
          background: "#fff",
          paddingTop: 12,
          marginTop: 0,
          borderTop:
            "1px solid #f1f5f9",
          zIndex: 2,
        }}
      >
        {smsBalance <= 0 && (
          <div
            style={{
              width: "100%",
              marginBottom: 12,
              padding: "12px",
              borderRadius: 8,
              background: "#fef2f2",
              border:
                "1px solid #fecaca",
              color: "#dc2626",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            SMS balance exhausted.
            You cannot register members
            until SMS balance is topped
            up.
          </div>
        )}

        <button
          className="btn"
          onClick={onClose}
          disabled={submitting}
        >
          Close
        </button>

        <button
          className="btn primary"
          onClick={submit}
          disabled={
            submitting ||
            smsBalance <= 0
          }
          style={{
            opacity:
              smsBalance <= 0
                ? 0.6
                : 1,
            cursor:
              smsBalance <= 0
                ? "not-allowed"
                : "pointer",
          }}
        >
          {submitting ? (
            <div className="btn-loader-wrap">
              <div className="btn-spinner" />

              <span>
                Submitting...
              </span>
            </div>
          ) : (
            "Submit"
          )}
        </button>
      </div>
    </div>
  );
}